import { useMemo } from "react";
import { Treemap, Tooltip, ResponsiveContainer } from "recharts";

function getMarginColor(margin, revenue) {
  const marginPercent = revenue > 0 ? (margin / revenue) * 100 : 0;
  if (marginPercent > 40) return "#8F8CE8";
  if (marginPercent >= 35) return "#A9D5FE";
  if (marginPercent >= 30) return "#7DDE7A";
  if (marginPercent >= 25) return "#B5F1A9";
  if (marginPercent >= 20) return "#DDF08A";
  if (marginPercent >= 15) return "#FBD08A";
  if (marginPercent >= 10) return "#FF9A9A";
  return "#C97A7A";
}

const CustomCell = ({ x, y, width, height, name, margin, revenue, onClick, hoveredItem, setHoveredItem }) => {
  if (width <= 2 || height <= 2) return null;
  const isHovered = hoveredItem === name;
  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={`${name}: open drilldown`}
      onClick={() => onClick?.(name)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(name);
        }
      }}
      style={{ cursor: "pointer" }}
      onMouseEnter={() => setHoveredItem?.(name)}
      onMouseLeave={() => setHoveredItem?.(null)}
    >
      <rect
        x={x} y={y} width={width} height={height}
        fill={getMarginColor(margin, revenue)}
        stroke="#fff"
        style={{
          transition: "all 0.18s ease",
          filter: isHovered ? "brightness(1.25) drop-shadow(0 6px 14px rgba(0,0,0,0.35))" : "brightness(1)",
          transform: isHovered ? "scale(1.01)" : "scale(1)",
          transformOrigin: "center",
        }}
        onMouseEnter={(e) => { e.target.style.filter = "brightness(1.15)"; setHoveredItem?.(name); }}
        onMouseLeave={(e) => { e.target.style.filter = "brightness(1)"; setHoveredItem?.(null); }}
      />
      {width > 70 && height > 28 && (
        <text
          x={x + width / 2} y={y + height / 2}
          textAnchor="middle" dominantBaseline="middle"
          pointerEvents="none" fill="#ffffff" fontWeight="600"
        >
          {name}
        </text>
      )}
    </g>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  const fmt = (v) => Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const marginPercent = data.revenue > 0 ? (data.margin / data.revenue) * 100 : 0;
  return (
    <div style={{
      background: "#ffffff", color: "black", padding: "10px 14px",
      borderRadius: 8, fontSize: 13, boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
      animation: "fadeIn 0.15s ease"
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{data.name}</div>
      <div>Revenue: {fmt(data.revenue)}</div>
      <div>Hours: {fmt(data.hours)}</div>
      <div>Overhead: {fmt(data.overhead)}</div>
      <div>Margin: {fmt(data.margin)} ({marginPercent.toFixed(1)}%)</div>
    </div>
  );
};

export default function ClientTreemap({ data = [], selectedClient, onClientClick, onBack, hoveredItem, setHoveredItem }) {
  const children = useMemo(
    () => (data || []).map(d => ({
      name: d.name,
      revenue: Number(d.revenue || 0),
      margin: Number(d.margin || 0),
      hours: Number(d.hours || 0),
      overhead: Number(d.overhead || 0),
    })),
    [data]
  );

  const treeData = useMemo(
    () => [{ name: selectedClient || "Clients", children }],
    [children, selectedClient]
  );

  if (!children.length) return <p>No data to display...</p>;

  return (
    <div style={{
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      padding: 16,
      height: "100%",
    }}>

      {/* Cabecera */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "#6b7280",
          }}>
            Client HeatMap
          </span>

          {selectedClient && (
            <>
              <span style={{ fontSize: 11, color: "#d1d5db" }}>·</span>
              <span style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#111827",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}>
                {selectedClient}
              </span>
              <button
                onClick={onBack}
                style={{
                  marginLeft: 6,
                  padding: "3px 10px",
                  borderRadius: 6,
                  border: "1px solid #e5e7eb",
                  background: "#f9fafb",
                  color: "#374151",
                  fontSize: 11,
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                ← Back
              </button>
            </>
          )}
        </div>

        <span style={{ fontSize: 10, color: "#9ca3af" }}>by revenue</span>
      </div>

      {/* Treemap */}
      <div style={{ width: "100%", aspectRatio: "1 / 1" }}>
        <ResponsiveContainer>
          <Treemap
            key={selectedClient || "root"}
            data={treeData}
            dataKey="revenue"
            nameKey="name"
            aspectRatio={4 / 3}
            type="squarify"
            isAnimationActive={false}
            content={(props) => (
              <CustomCell
                {...props}
                onClick={onClientClick}
                hoveredItem={hoveredItem}
                setHoveredItem={setHoveredItem}
              />
            )}
          >
            <Tooltip content={<CustomTooltip />} />
          </Treemap>
        </ResponsiveContainer>
      </div>
    </div>
  );
}